import type { CSSProperties } from 'react';
import { useState } from 'react';

import { SectionCard } from '../../regional/BucketStates';

import { RegionColorPicker } from './RegionColorPicker';
import { buildColorMap, ROOT_COLORS } from './RegionHierarchyTree';
import { flattenById, resolveRoot } from './regionRoot';
import {
  useRegionTree,
  useRenameRegion,
  useSetRegionDirector,
  useSetRegionColor,
} from './regionsQueries';
import { directorOptions, useRegionalDirectors } from './useRegionalDirectors';

interface Props {
  regionId: string;
  name: string;
  directorUserId: string | null;
}

const labelStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const eyebrow = { fontSize: 9 } as const;
const fieldStyle: CSSProperties = {
  height: 34,
  padding: '0 11px',
  borderRadius: 9,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  font: 'inherit',
  fontSize: 13,
  color: 'var(--nb-ink)',
  boxSizing: 'border-box',
};

/** Edit a region's name and assign/clear its regional director (BON-153). */
export function RegionSettingsCard({ regionId, name, directorUserId }: Props) {
  const [draftName, setDraftName] = useState(name);
  const rename = useRenameRegion();
  const setDirector = useSetRegionDirector();
  const setColor = useSetRegionColor();
  const rdQ = useRegionalDirectors();
  const directors = directorOptions(rdQ.data ?? [], directorUserId);
  const treeQ = useRegionTree();

  const trimmed = draftName.trim();
  const nameDirty = trimmed.length > 0 && trimmed !== name;

  // Color lives on the root region; resolve it from the tree so a subregion's
  // picker targets (and reflects) its root.
  const regions = treeQ.data ?? [];
  const colorMap = buildColorMap(regions);
  const root = resolveRoot(flattenById(regions), regionId);
  const currentColor = (root.rootId && colorMap.get(root.rootId)) || ROOT_COLORS[0];

  return (
    <SectionCard title="Region settings">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={labelStyle}>
          <span className="nb-eyebrow" style={eyebrow}>
            Name
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              aria-label="Region name"
              style={{ ...fieldStyle, flex: 1 }}
            />
            <button
              type="button"
              className="nb-btn nb-btn-primary"
              style={{ fontSize: 12 }}
              disabled={!nameDirty || rename.isPending}
              onClick={() => rename.mutate({ id: regionId, name: trimmed })}
            >
              {rename.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <label style={labelStyle}>
          <span className="nb-eyebrow" style={eyebrow}>
            Regional director
          </span>
          <select
            value={directorUserId ?? ''}
            disabled={setDirector.isPending || rdQ.isLoading}
            onChange={(e) => setDirector.mutate({ id: regionId, userId: e.target.value || null })}
            style={fieldStyle}
          >
            <option value="">— Unassigned —</option>
            {directors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        <RegionColorPicker
          rootName={root.rootName}
          isRootSelected={root.isRootSelected}
          currentColor={currentColor}
          saving={setColor.isPending}
          onPick={(color) => root.rootId && setColor.mutate({ id: root.rootId, color })}
        />
      </div>
    </SectionCard>
  );
}
